/**
 * Safe rich-text tokenizer for Copilot Sales replies (MASTER.md AA-21).
 * Turns the model's plain-text answer into tokens so the chat can render
 * clickable citations without ever using dangerouslySetInnerHTML — the
 * component maps each token to a React node, so all text is escaped by
 * React and only vetted http(s) links become anchors.
 *
 * Supports:
 *  - Markdown links: [texto](https://…)
 *  - Bare URLs: https://… or http://…
 * Only http/https are linkified. javascript:, data:, mailto:, relative or
 * malformed targets stay as inert text — no clickable surface for injection.
 */
export type RichTextToken =
  | { type: 'text'; value: string }
  | { type: 'link'; text: string; href: string }

function isSafeHttpUrl(raw: string): boolean {
  try {
    const u = new URL(raw)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

// Markdown link OR a bare http(s) URL. Markdown alternative first so a URL
// already inside [..](..) isn't double-matched.
const TOKEN_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"')\]]+)/gi

export function parseRichText(input: string): RichTextToken[] {
  const text = String(input ?? '')
  const tokens: RichTextToken[] = []
  let lastIndex = 0
  for (const match of text.matchAll(TOKEN_RE)) {
    const index = match.index ?? 0
    if (index > lastIndex) tokens.push({ type: 'text', value: text.slice(lastIndex, index) })

    const [whole, mdText, mdHref, bareUrl] = match
    if (mdHref && isSafeHttpUrl(mdHref)) {
      tokens.push({ type: 'link', text: mdText, href: mdHref })
    } else if (bareUrl && isSafeHttpUrl(stripTrailingPunctuation(bareUrl).url)) {
      const { url, trailing } = stripTrailingPunctuation(bareUrl)
      tokens.push({ type: 'link', text: url, href: url })
      if (trailing) tokens.push({ type: 'text', value: trailing })
    } else {
      // Unsafe or malformed — keep the raw matched text inert.
      tokens.push({ type: 'text', value: whole })
    }
    lastIndex = index + whole.length
  }
  if (lastIndex < text.length) tokens.push({ type: 'text', value: text.slice(lastIndex) })
  return tokens
}

/** Trailing sentence punctuation shouldn't be swallowed into a bare URL. */
function stripTrailingPunctuation(url: string): { url: string; trailing: string } {
  const m = url.match(/[.,;:!?)]+$/)
  if (!m) return { url, trailing: '' }
  return { url: url.slice(0, url.length - m[0].length), trailing: m[0] }
}
